import { useState } from "react";
import { cn } from "@/lib/utils";

type EvidenceKey = "global" | "spain" | "survey";

interface Metric {
  value: string;
  description: string;
  detail?: string;
  source: string;
  accent?: boolean;
}

interface EvidenceGroup {
  label: string;
  badge: string;
  primary: Metric;
  secondary: Metric[];
  disclaimer?: string;
}

const evidence: Record<EvidenceKey, EvidenceGroup> = {
  global: {
    label: "Global",
    badge: "GLOBAL",
    primary: {
      value: "20%",
      description: "de los empleados en el mundo se sienten comprometidos con su trabajo.",
      source: "Gallup, State of the Global Workplace 2026",
    },
    secondary: [
      {
        value: "41%",
        description: "de los empleados experimenta estrés diario.",
        source: "Gallup 2026",
      },
      {
        value: "$10T",
        description: "pierde la economía global por falta de compromiso laboral.",
        source: "Gallup 2026",
        accent: true,
      },
    ],
  },
  spain: {
    label: "España",
    badge: "ESPAÑA",
    primary: {
      value: "20,3%",
      description: "del absentismo estructural en España se debe a salud mental y riesgos psicosociales.",
      source: "Consejo General de Graduados Sociales de España, 2026",
    },
    secondary: [
      {
        value: "+70%",
        description: "de las ausencias laborales se deben a causas personales, familiares o emocionales.",
        source: "Vivofácil, “¿Por qué no vas al trabajo?”",
        accent: true,
      },
    ],
  },
  survey: {
    label: "Encuesta",
    badge: "ENCUESTA PROPIA",
    primary: {
      value: "76%",
      description: "experimenta emociones negativas varias veces por semana en el trabajo.",
      source: "Encuesta Hummany, 36 personas · jun. 2026",
    },
    secondary: [
      {
        value: "58%",
        description: "menciona la carga de trabajo como causa principal de su malestar.",
        detail: "El 47% menciona la falta de reconocimiento.",
        source: "Encuesta Hummany, 36 personas · jun. 2026",
      },
      {
        value: "56%",
        description: "usaría una plataforma como Hummany si tuviera acceso a ella.",
        source: "Encuesta Hummany, 36 personas · jun. 2026",
        accent: true,
      },
    ],
    disclaimer: "Encuesta propia de validación de producto, no un estudio poblacional. Realizada a 36 personas de diferentes edades, empresas, sectores y roles profesionales.",
  },
};

const tabOrder: EvidenceKey[] = ["global", "spain", "survey"];

const badgeClass: Record<EvidenceKey, string> = {
  global: "bg-primary/10 text-primary",
  spain: "bg-secondary/15 text-secondary",
  survey: "bg-calm text-calm-foreground",
};

export function Problem() {
  const [activeTab, setActiveTab] = useState<EvidenceKey>("global");
  const group = evidence[activeTab];
  const isSpain = activeTab === "spain";
  const activeTabIndex = tabOrder.indexOf(activeTab);

  return (
    <section id="problema" className="relative bg-surface py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">El problema, en datos</p>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Las personas no se queman <br className="hidden sm:block" /> una vez al año. <span className="text-gradient-brand">Se queman cada día.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            El estrés, la desconexión y el burnout impactan directamente en la productividad, el compromiso y la retención del talento.
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Ámbito de los datos"
          className="relative mx-auto mt-9 grid max-w-md grid-cols-3 gap-1 rounded-full bg-muted/70 p-1"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-1 left-1 w-[calc((100%_-_0.5rem)/3)] rounded-full bg-card shadow-sm transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
            style={{ transform: `translateX(calc(${activeTabIndex * 100}% + ${activeTabIndex * 0.25}rem))` }}
          />
          {tabOrder.map((key) => (
            <button
              key={key}
              id={`evidence-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              aria-controls="evidence-panel"
              onClick={() => setActiveTab(key)}
              className={cn(
                "relative z-10 rounded-full px-3 py-2.5 text-sm font-medium transition-colors duration-[420ms]",
                activeTab === key
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {evidence[key].label}
            </button>
          ))}
        </div>

        <div
          id="evidence-panel"
          role="tabpanel"
          aria-labelledby={`evidence-tab-${activeTab}`}
          key={activeTab}
          className="evidence-panel-enter mt-9"
        >
          <div className={cn("grid gap-5 lg:min-h-[380px]", isSpain ? "lg:grid-cols-2" : "lg:grid-cols-3")}>
            <div
              className={cn(
                "relative flex min-h-[250px] flex-col justify-center overflow-hidden rounded-3xl border border-border bg-card p-8 shadow-soft sm:p-10",
                !isSpain && "lg:col-span-2"
              )}
            >
              <div className="blur-blob -bottom-20 -right-12 h-64 w-64 bg-primary/20" />
              <div className="relative max-w-2xl">
                <span className={cn("inline-flex rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide", badgeClass[activeTab])}>
                  {group.badge}
                </span>
                <p className={cn(
                  "mt-4 font-semibold tracking-tight text-gradient-brand",
                  isSpain ? "text-5xl sm:text-6xl" : "text-6xl sm:text-7xl"
                )}>
                  {group.primary.value}
                </p>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground sm:text-lg">
                  {group.primary.description}
                </p>
                <p className="mt-3 text-xs text-muted-foreground">Fuente: {group.primary.source}</p>
              </div>
            </div>

            <div className={cn("flex gap-5", !isSpain && "flex-col")}>
              {group.secondary.map((metric) => (
                <div
                  key={metric.value}
                  className={cn(
                    "flex flex-1 flex-col justify-center rounded-3xl",
                    isSpain ? "p-8 sm:p-10" : "p-6",
                    metric.accent
                      ? "gradient-soft"
                      : "border border-border bg-card shadow-soft"
                  )}
                >
                  <span className={cn(
                    "w-fit rounded-full font-semibold tracking-wide",
                    isSpain ? "px-3 py-1 text-[11px]" : "px-2.5 py-1 text-[10px]",
                    badgeClass[activeTab]
                  )}>
                    {group.badge}
                  </span>
                  <p className={cn(
                    "font-semibold tracking-tight",
                    isSpain ? "mt-4 text-5xl text-gradient-brand sm:text-6xl" : "mt-3 text-4xl text-foreground"
                  )}>{metric.value}</p>
                  <p className={cn(
                    "leading-relaxed text-foreground",
                    isSpain ? "mt-4 text-base sm:text-lg" : "mt-3 text-sm"
                  )}>{metric.description}</p>
                  {metric.detail && <p className="mt-2 text-xs text-muted-foreground">{metric.detail}</p>}
                  <p className="mt-2 text-xs text-muted-foreground">Fuente: {metric.source}</p>
                </div>
              ))}
            </div>
          </div>

          {group.disclaimer && (
            <p className="mt-5 rounded-2xl border border-dashed border-calm-foreground/25 bg-calm/30 px-5 py-3 text-sm leading-relaxed text-calm-foreground">
              {group.disclaimer}
            </p>
          )}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-xl font-medium text-foreground">
          “El malestar no siempre se ve. <span className="text-muted-foreground">Pero las empresas sí lo sienten.”</span>
        </p>
      </div>
    </section>
  );
}
